// Propositions — the idle Lab's three ideas, as CHIPS stacked ABOVE the
// composer (the user's reference screen, 2026-09-11): each question in its
// own frosted pill, as wide as its words, one under the other at the left
// edge, the way a messenger offers quick replies over its field. Nothing
// but the sentence — no icon, no type tag. The tag that closed each
// sentence (2026-09-08, the app's `.typechip` in the item type's colour)
// stood down with the redesign: the reference chips carry words alone, and
// a coloured tag on a frosted pill over a gradient read as a second voice.
// Before the chips the ideas were quiet full-width rows (2026-09-08), and
// before that tinted icon discs (2026-09-07). What survives of that lineage
// is data, not pixels: `TYPE_TAGS` still names the question types for the
// recents drawer, `TYPE_ART` keeps each type's identity colour and picture
// (the type-identity registry the tests pin) and `tagLabel` still shortens
// an item type's name — none of them rendered here.
import { tmeta } from "config/vocab";

// Featured = the first entry of each question TYPE in pack order, three at
// most, so the trio reads as a spread of what the Lab can do; the rest of
// the pack stays reachable through the recents and the briefing CTAs.
//
// The one colour on the row is the CULTURAL ITEM TYPE tag closing each
// sentence (user call 2026-09-08, later the same day: "small tags for
// cultural item type in color like used in app"). That is a different
// taxonomy from the question types above and the reason the surface can
// carry it after the icon chips were taken off: TYPE_TAGS/TYPE_ART name what
// the Lab is DOING (Archive lab, Content lab), while this names what the
// answer is ABOUT in the vocabulary the rest of the app already speaks —
// `config/vocab.TYPE_META`, the same `.typechip` the Top drivers list and
// the Lab's own plays wear. It is authored per entry (`itemType` in
// mock/lab/<brand>.js), never derived from the question type: a "geo"
// question can be about community and an "audience" one about a capsule.
// An entry with no `itemType` simply gets no tag.
export const TYPE_TAGS = {
  competitor: "Rival watch",
  goal: "Growth lab",
  general: "Quarter plan",
  retail: "Site scout",
  audience: "Audience lab",
  geo: "Market scan",
  forecast: "Foresight",
  collab: "Collab scout",
  celeb: "Talent scout",
  archive: "Archive lab",
  experience: "Experience lab",
  content: "Content lab",
  fans: "Fan lab",
  fandom: "Fandom lab",
  community: "Community lab",
  momentum: "Momentum lab",
  revival: "Revival lab",
  signal: "Signal lab",
};

// per type: the disc/icon colour + the picture. `fill` paths are painted
// as a soft area (currentColor at low opacity), `d` paths are strokes;
// `dash` dashes a stroke. 24-unit box, 1.5 strokes.
export const TYPE_ART = {
  // binoculars — watching the rival (event coral)
  competitor: {
    color: "#FF5A76",
    fill: [
      "M7.5 10.5a3.5 3.5 0 1 0 0 7 3.5 3.5 0 0 0 0-7z",
      "M16.5 10.5a3.5 3.5 0 1 0 0 7 3.5 3.5 0 0 0 0-7z",
    ],
    d: [
      "M7.5 10.5a3.5 3.5 0 1 0 0 7 3.5 3.5 0 0 0 0-7z",
      "M16.5 10.5a3.5 3.5 0 1 0 0 7 3.5 3.5 0 0 0 0-7z",
      "M11 13h2",
      "M6 10.5V6.5h3v4M15 10.5v-4h3v4",
    ],
  },
  // three bars climbing, an arrow over them (experience green)
  goal: {
    color: "#4ADE80",
    fill: ["M4 20v-5h3.5v5z", "M10.25 20v-9h3.5v9z", "M16.5 20v-13H20v13z"],
    d: [
      "M4 20v-5h3.5v5z",
      "M10.25 20v-9h3.5v9z",
      "M16.5 20v-13H20v13z",
      "M3 10.5l5.5-5 4 3L20 3",
      "M16.5 3H20v3.5",
    ],
  },
  // a calendar with the quarter's day marked (styling slate)
  general: {
    color: "#94A3B8",
    fill: ["M4 6.5h16v3.5H4z", "M13.5 13.5H17v3.5h-3.5z"],
    d: [
      "M4 6.5h16v13.5H4z",
      "M4 10h16",
      "M8 4v4M16 4v4",
      "M13.5 13.5H17v3.5h-3.5z",
    ],
  },
  // a storefront — awning, door (merch amber)
  retail: {
    color: "#FFB74D",
    fill: ["M3 8.5l1.8-4h14.4l1.8 4v2.5H3z", "M10 20v-5h4v5z"],
    d: [
      "M3 8.5l1.8-4h14.4l1.8 4v2.5H3z",
      "M5 11v9h14v-9",
      "M10 20v-5h4v5",
      "M3 8.5h18",
    ],
  },
  // a crowd — two people in front, filled (community teal)
  audience: {
    color: "#2DD4BF",
    fill: [
      "M8 6.5a2.75 2.75 0 1 0 0 5.5 2.75 2.75 0 0 0 0-5.5z",
      "M16 6.5a2.75 2.75 0 1 0 0 5.5 2.75 2.75 0 0 0 0-5.5z",
    ],
    d: [
      "M8 6.5a2.75 2.75 0 1 0 0 5.5 2.75 2.75 0 0 0 0-5.5z",
      "M16 6.5a2.75 2.75 0 1 0 0 5.5 2.75 2.75 0 0 0 0-5.5z",
      "M2.5 20a5.5 5.5 0 0 1 11 0",
      "M10.5 20a5.5 5.5 0 0 1 11 0",
    ],
  },
  // a globe, one hemisphere shaded (brand-codes blue)
  geo: {
    color: "#60A5FA",
    fill: ["M12 3.5a8.5 8.5 0 0 0 0 17z"],
    d: [
      "M12 3.5a8.5 8.5 0 1 0 0 17 8.5 8.5 0 0 0 0-17z",
      "M3.5 12h17",
      "M12 3.5c3.4 2.6 3.4 14.4 0 17c-3.4-2.6-3.4-14.4 0-17z",
    ],
  },
  // a measured line running into a dashed projection, the past shaded
  // (content sky)
  forecast: {
    color: "#4FC3F7",
    fill: ["M3 19v-4.5l5-4 4 3 4-6v11.5z"],
    d: ["M3 14.5l5-4 4 3 4-6", "M3 19h18"],
    dash: ["M16 7.5l5-3"],
  },
  // two rings, the overlap filled — the partnership (collab lavender)
  collab: {
    color: "#B388FF",
    fill: ["M12 8a5 5 0 0 1 0 8a5 5 0 0 1 0-8z"],
    d: [
      "M9 7a5 5 0 1 0 0 10 5 5 0 0 0 0-10z",
      "M15 7a5 5 0 1 0 0 10 5 5 0 0 0 0-10z",
    ],
  },
  // a star with a spark — the name on the marquee (art-direction pink)
  celeb: {
    color: "#F472B6",
    fill: [
      "M11 4.5l2.3 4.7 5.2.8-3.8 3.6.9 5.2-4.6-2.5-4.6 2.5.9-5.2L3.5 10l5.2-.8z",
    ],
    d: [
      "M11 4.5l2.3 4.7 5.2.8-3.8 3.6.9 5.2-4.6-2.5-4.6 2.5.9-5.2L3.5 10l5.2-.8z",
      "M19.5 3v3M18 4.5h3",
    ],
  },
  // an archive box, lid on, a tag hanging from the handle — the house's own
  // past brought back (archive tan, the type's own colour)
  archive: {
    color: "#D7A97C",
    fill: ["M3.5 5h17v3.5h-17z", "M10 12h4v2.5h-4z"],
    d: [
      "M4.5 8.5h15V20h-15z",
      "M3.5 5h17v3.5h-17z",
      "M10 12h4v2.5h-4z",
      "M14 14.5l1.5 3.5h-3z",
    ],
  },
  // a ticket stub — the notched edges, the perforation, the seat marked:
  // where the house shows up in culture, and whether the crowd can get in
  // (capsule yellow — the one identity colour no other type wears)
  experience: {
    color: "#FDE047",
    fill: ["M3.5 7.5h17v3a2 2 0 0 0 0 4v3h-17v-3a2 2 0 0 0 0-4z"],
    d: [
      "M3.5 7.5h17v3a2 2 0 0 0 0 4v3h-17v-3a2 2 0 0 0 0-4z",
      "M7 11.5h4M7 14h6",
    ],
    dash: ["M15.5 8v9"],
  },
  // a clip — a framed play mark over a scrub bar: the piece the feed passes
  // along (the app's violet, vocab's type-identity fallback — the eleven
  // item-type colours are all taken by the types above)
  content: {
    color: "#8b7bff",
    fill: ["M10 8.5v7l6-3.5z"],
    d: ["M3.5 5.5h17v12h-17z", "M10 8.5v7l6-3.5z"],
    dash: ["M3.5 20.5h17"],
  },
  // a heart over a ladder rung — the fan ladder (2026-09-21, the Starbucks
  // fans question; a pink no item type wears)
  fans: {
    color: "#FF7AB8",
    fill: [
      "M12 19.5l-6.2-6.4a3.6 3.6 0 0 1 5.1-5.1L12 9.1l1.1-1.1a3.6 3.6 0 0 1 5.1 5.1z",
    ],
    d: [
      "M12 19.5l-6.2-6.4a3.6 3.6 0 0 1 5.1-5.1L12 9.1l1.1-1.1a3.6 3.6 0 0 1 5.1 5.1z",
      "M3.5 21.5h17",
    ],
    dash: ["M3.5 4h17"],
  },
  // a flame over a base line — the fandom that burns and the platform it
  // sits on (2026-09-21, the Starbucks fandom question; a tangerine no item
  // type wears)
  fandom: {
    color: "#FB923C",
    fill: [
      "M12 3.5c1.2 3 3.8 4.6 3.8 8.2a3.8 3.8 0 0 1-7.6 0c0-1.3.5-2.3 1.2-3.2.3 1 .9 1.7 1.7 2 .1-2.8.3-5 .9-7z",
    ],
    d: [
      "M12 3.5c1.2 3 3.8 4.6 3.8 8.2a3.8 3.8 0 0 1-7.6 0c0-1.3.5-2.3 1.2-3.2.3 1 .9 1.7 1.7 2 .1-2.8.3-5 .9-7z",
      "M5.5 18.5h13",
    ],
    dash: ["M3.5 21.5h17"],
  },
  // two heads round a cup on a table — the community and the room it sits
  // in (2026-09-21, the Starbucks community question; a lime no item type
  // wears — the community type's own teal is taken by the experience lab)
  community: {
    color: "#A3E635",
    fill: [
      "M6.5 6.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5z",
      "M17.5 6.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5z",
    ],
    d: [
      "M6.5 6.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5z",
      "M17.5 6.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5z",
      "M9.5 13.5h5v3a2.5 2.5 0 0 1-5 0z",
      "M14.5 14.5h1.2a1.3 1.3 0 0 1 0 2.6h-1.2",
    ],
    dash: ["M3.5 21.5h17"],
  },
  // a line climbing to an arrowhead over a flat dashed base — one category
  // gaining while the other stands still (2026-09-25, the Estée Lauder
  // momentum question; a fuchsia no item type wears)
  momentum: {
    color: "#E879F9",
    fill: ["M4 18l5-5 3 3 8-8v10H4z"],
    d: ["M4 18l5-5 3 3 8-8", "M16 8h4v4"],
    dash: ["M4 21h16"],
  },
  // a cuff — an open band with its hallmark line under it: a category of the
  // house's own brought back as design (2026-10-08, the Tiffany silver
  // question "Should we bring back silver and how?"; a sterling grey no item
  // type wears — the slate #94A3B8 is the quarter plan's)
  revival: {
    color: "#A1A1AA",
    fill: [
      "M12 5.5a7.5 7.5 0 0 1 7.5 7.5v1.5h-3V13a4.5 4.5 0 0 0-9 0v1.5h-3V13A7.5 7.5 0 0 1 12 5.5z",
    ],
    d: [
      "M12 5.5a7.5 7.5 0 0 1 7.5 7.5v1.5h-3V13a4.5 4.5 0 0 0-9 0v1.5h-3V13A7.5 7.5 0 0 1 12 5.5z",
      "M9 18.5h6",
    ],
    dash: ["M3.5 21.5h17"],
  },
  // a sparkle over a rising pulse line — an ingredient's trail read
  // before anyone on the board has played it (2026-10-23, the Starbucks
  // collagen question "Is collagen about to shake up the beverage bar?",
  // the Home's second Early Signal card; a cyan no item type wears —
  // content's sky #4FC3F7 is the nearest)
  signal: {
    color: "#22D3EE",
    fill: [
      "M5.5 2l1 2.5 2.5 1-2.5 1-1 2.5-1-2.5L2 5.5l2.5-1z",
      "M3 16l4-3 3 3 4-7 3 4 4-5V19H3z",
    ],
    d: [
      "M3 16l4-3 3 3 4-7 3 4 4-5",
      "M5.5 2l1 2.5 2.5 1-2.5 1-1 2.5-1-2.5L2 5.5l2.5-1z",
    ],
    dash: ["M3 21h18"],
  },
  // a flask with something in it — the fallback (archive tan)
  lab: {
    color: "#D7A97C",
    fill: ["M7.4 15h9.2l2.3 4.1a.8.8 0 0 1-.7 1.2H5.8a.8.8 0 0 1-.7-1.2z"],
    d: [
      "M10 3v6l-5.5 9.5A1.5 1.5 0 0 0 5.8 21h12.4a1.5 1.5 0 0 0 1.3-2.5L14 9V3",
      "M9 3h6",
    ],
  },
};

export const typeArt = (type) => TYPE_ART[type] || TYPE_ART.lab;

// The tag's label is the vocabulary's, SHORTENED where the full name is a
// mouthful at the end of a sentence (user call 2026-09-08: "make archive
// reissue just archive"). Display only, and only here: `config/vocab`
// keeps "Archive re-issue" for the drivers list, Score DNA, the stats
// screens and the advisor, where the tag is the row's subject rather than
// a footnote to one.
const TAG_SHORT = { archive_reissue: "Archive" };
export const tagLabel = (itemType) => TAG_SHORT[itemType] || tmeta(itemType).n;

// the type's duotone picture — no longer on the idea rows (2026-09-08),
// kept as the registry's renderer for wherever a type needs its mark
export function TypeIcon({ type }) {
  const art = typeArt(type);
  return (
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {(art.fill || []).map((d) => (
        <path
          key={`f${d}`}
          d={d}
          fill="currentColor"
          fillOpacity="0.24"
          stroke="none"
        />
      ))}
      {(art.d || []).map((d) => (
        <path key={d} d={d} />
      ))}
      {(art.dash || []).map((d) => (
        <path key={`s${d}`} d={d} strokeDasharray="2 2.2" />
      ))}
    </svg>
  );
}

// four since 2026-09-22 (user call on the collab question: "one more mock
// question for starbucks, so there is one more in recommended") — three from
// the redesign's reference screen until then. Starbucks carries four question
// types; UGG three. Tiffany carries seven entries and shows THREE chips —
// the silver question first, then the fan lab and the collab scout
// (`chips: 3` on the pack, see below) — since 2026-10-12 (user call: "show
// just 3 questions in ai lab in ui, so comment out others, but keep code";
// FIVE with the archive and market labs from 2026-10-06 / 07 / 08, user calls:
// "remove highest engagement content proposed question from ui", then
// "where in culture should we show up next, remove it from recommended",
// then the collab scout added as the fifth entry that evening and the
// market lab as the sixth the next day): an entry with
// `featured: false` is skipped by BOTH passes below but stays in the pack,
// so a typed question, an alias and a briefing card's deep link still land
// on its answer. Since 2026-10-08 a pack may carry its OWN count (`chips` on
// the pack — Tiffany showed FIVE when the silver question joined as its
// seventh entry, user call: "add one more mock question", and THREE since
// 2026-10-12); a pack without one keeps this default, so Starbucks' four
// and UGG's three are untouched.
export const FEATURED = 4;

export function featuredEntries(all, n = FEATURED) {
  const entries = (all || []).filter((e) => e.featured !== false);
  const seen = new Set();
  const picks = [];
  entries.forEach((e) => {
    if (picks.length === n || seen.has(e.type)) return;
    seen.add(e.type);
    picks.push(e);
  });
  // fewer types than slots → fill from the remaining entries, pack order
  entries.forEach((e) => {
    if (picks.length < n && !picks.includes(e)) picks.push(e);
  });
  return picks;
}

export default function Starters({ entries, chips, onAsk }) {
  const picks = featuredEntries(entries, chips || FEATURED);
  if (!picks.length) return null;
  return (
    <section className="lab-props" aria-label="Ideas to consider">
      {/* no label and no mark over the chips: the corner arrow that stood
          where the "3 ideas to consider" subtitle had been (2026-09-08)
          went with the chip form — the reference screen opens straight on
          the three pills. The section keeps its aria-label, so a screen
          reader still meets the chips as a named group. */}
      <div className="lab-ideas">
        {picks.map((e) => (
          <button
            type="button"
            className="lab-idea"
            key={e.id}
            onClick={() => onAsk(e.question, { from: "starter" })}
          >
            <span className="lab-idea-t">{e.question}</span>
          </button>
        ))}
      </div>
    </section>
  );
}
